import type { HTMLAttributes } from 'react';
import { clsx } from 'clsx';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'brand';
  size?: 'sm' | 'md';
  /**
   * Calm-v2 styling: soft semantic background + strong text, no border,
   * small radius (rounded-chip/badge), 10–11px/700. Only valid in apps that
   * import `@elka/theme` v2/calm.css. Default (false) keeps legacy styling.
   */
  calm?: boolean;
}

export function Badge({
  children,
  variant = 'default',
  size = 'md',
  calm = false,
  className,
  ...props
}: BadgeProps) {
  const variants = {
    default: 'bg-gray-100 text-gray-800',
    success: 'bg-emerald-100 text-emerald-800',
    warning: 'bg-amber-100 text-amber-800',
    danger: 'bg-red-100 text-red-800',
    info: 'bg-sky-100 text-sky-800',
    brand: 'bg-[#E9F0FF] text-[#0C1A55]',
  };

  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
  };

  const calmVariants = {
    default: 'bg-line text-ink-2',
    success: 'bg-success-soft text-success',
    warning: 'bg-warn-soft text-warn',
    danger: 'bg-danger-soft text-danger',
    info: 'bg-info-soft text-info',
    brand: 'bg-brand-soft text-brand',
  };

  const calmSizes = {
    sm: 'rounded-badge px-1.5 py-px text-[10px]',
    md: 'rounded-chip px-2 py-0.5 text-[11px]',
  };

  if (calm) {
    return (
      <span
        className={clsx(
          'inline-flex shrink-0 items-center gap-1 font-bold whitespace-nowrap',
          calmVariants[variant],
          calmSizes[size],
          className
        )}
        {...props}
      >
        {children}
      </span>
    );
  }

  return (
    <span
      className={clsx(
        'inline-flex shrink-0 items-center rounded-full font-medium whitespace-nowrap',
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
}
